import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import "rc-time-picker/assets/index.css";
import { Modal, ModalBody, Spinner } from "reactstrap";
import crossIcon from "assets/images/cross.svg";
import Text from "components/Text";
import styles from "../AddEvent.module.scss";
import userDefaultImage from "assets/images/staff-default-rounded.png";
import Select from "react-select";
import { usePolicyAssigneeList } from "repositories/office-policy-management-repository";
import { useStaffDesignation } from "repositories/scheduler-repository";

const defaultOfficeValue = {
  value: 0,
  label: "All Offices",
};

const defaultRoleValue = {
  value: "",
  label: "All Roles",
};

function SelectEmployeeForPolicyVersionModal({
  t,
  issaveEmployeeModalOpen,
  setIsSaveEmployeeModalOpen,
  memberIds,
  setMemberIds,
  // New props for API integration
  policyFolderId,
  ownerId,
  policyId,
  isManagerOnly = false,
  officeIds = [],
  dontCloseModal = false,
  confirmLoading = false,
}) {
  const [search, setSearch] = useState("");
  const [membersList, setMembersList] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState(memberIds || []);
  const [selectedOffice, setSelectedOffice] = useState(defaultOfficeValue);
  const [selectedRole, setSelectedRole] = useState(defaultRoleValue);
  const [rolesData, setRolesData] = useState([]);
  const [officeList, setOfficeList] = useState([defaultOfficeValue]);
  const [selectionError, setSelectionError] = useState("");

  // Determine if we should use API or prop data
  const useApi = !!(policyFolderId && ownerId);
  // Fetch staff data from API
  const { data: apiData, isLoading: isLoadingApi,isFetching: isFetchingApi } = usePolicyAssigneeList(
    policyFolderId,
    ownerId,
    {
      officeIds:
        selectedOffice.value && selectedOffice.value !== 0
          ? [selectedOffice.value]
          : officeIds.length > 0
            ? officeIds.map(({id,officeId}) => Number(id||officeId))
            : undefined,
      isManagerOnly,
      designationId:
        selectedRole.value && selectedRole.value !== ""
          ? selectedRole.value
          : undefined,
      policyId,
    },
    {
      enabled: useApi,
    }
  );

  // Fetch designation/role data
  const { isLoading: loadingRoleData, data: staffRoleData } =
    useStaffDesignation({ selectedId: ownerId }, { enabled: !!ownerId });

  // Process office data when it's loaded
  useEffect(() => {
    if ( officeIds?.length) {
      setOfficeList([defaultOfficeValue, ...officeIds.map(({name,id}) => ({label: name, value: id}))]);
    }
  }, [officeIds]);

  // Process role data when it's loaded
  useEffect(() => {
    if (!loadingRoleData && staffRoleData?.length) {
      const flatData = staffRoleData?.map((item) => item?.designations)?.flat();
      if (flatData?.length) {
        const formattedData = flatData.map((item) => ({
          label: item?.name,
          value: item?.id,
        }));
        setRolesData([defaultRoleValue, ...formattedData]);
      }
    }
  }, [loadingRoleData, staffRoleData]);

  // Process member data - use API data if available, otherwise use prop
  useEffect(() => {
    if (useApi) {
      const data = apiData || [];
      setMembersList(data);
    }
  }, [useApi, apiData]);

  // Reset filters and initialize selected members when modal opens
  useEffect(() => {
    if (issaveEmployeeModalOpen) {
      setSelectedMembers(memberIds || []);
      setSearch("");
      setSelectedOffice(defaultOfficeValue);
      setSelectedRole(defaultRoleValue);
      setSelectionError("");
    }
  }, [issaveEmployeeModalOpen, memberIds]);

  const onSearch = (val) => {
    setSearch(val);
    // Search is handled client-side on the current membersList
    // The API filtering is handled by the hook parameters
  };

  // Filter members by search term (client-side)
  const getFilteredMembers = () => {
    if (!search.trim()) {
      return membersList;
    }
    const trimmedSearchValue = search.replace(/\s+/g, "").toLowerCase();
    return membersList.filter((item) => {
      // Handle both API data structure (user.firstName) and prop data structure (firstName)
      const firstName = item?.user?.firstName || item?.firstName || "";
      const lastName = item?.user?.lastName || item?.lastName || "";
      const fullName = `${firstName}${lastName}`.toLowerCase();
      const reversedFullName = `${lastName}${firstName}`.toLowerCase();
      return (
        fullName.includes(trimmedSearchValue) ||
        reversedFullName.includes(trimmedSearchValue)
      );
    });
  };

  const filteredMembers = getFilteredMembers();

  const checkMember = (member) => {
    const selectedMemberIds = [...selectedMembers];
    // Handle both API data structure and prop data structure
    const memberId = member?.userId;
    const dIndex = selectedMemberIds.findIndex(
      (v) => Number(v.userId) === Number(memberId)
    );
    if (dIndex > -1) {
      selectedMemberIds.splice(dIndex, 1);
    } else {
      const existingPrefill = (memberIds || []).find(
        (v) => Number(v.userId) === Number(memberId),
      );
      selectedMemberIds.push({
        ...member,
        ...(existingPrefill?.id ? { id: existingPrefill.id } : {}),
        officeId: existingPrefill?.officeId ?? member?.officeId,
        offices: existingPrefill?.offices?.length
          ? existingPrefill.offices
          : member?.offices,
      });
    }
    setSelectedMembers([...selectedMemberIds]);
    setSelectionError("");
  };

  const addDefaultSrc = (ev) => {
    ev.target.src = userDefaultImage;
    ev.target.onerror = null;
  };

  const checkMemberRoleAvaliable = (array, value) => {
    const valueId =value?.userId;
    return array.some((e) => Number(e.userId) === Number(valueId));
  };

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedMembers(
        filteredMembers.map((member) => {
          const existingPrefill = (memberIds || []).find(
            (v) => Number(v.userId) === Number(member?.userId),
          );
          return {
            ...member,
            ...(existingPrefill?.id ? { id: existingPrefill.id } : {}),
            officeId: existingPrefill?.officeId ?? member?.officeId,
            offices: existingPrefill?.offices?.length
              ? existingPrefill.offices
              : member?.offices,
          };
        }),
      );
    } else {
      setSelectedMembers([]);
    }
    setSelectionError("");
  };
  return (
    <Modal
      isOpen={issaveEmployeeModalOpen}
      toggle={() => setIsSaveEmployeeModalOpen(false)}
      className="modal-dialog-centered modal-lg"
      modalClassName="custom-modal"
    >
      <span
        className="close-btn"
        onClick={() => setIsSaveEmployeeModalOpen(false)}
      >
        <img src={crossIcon} alt="close" />
      </span>
      <ModalBody className={styles["add-employee-modal-dialog"]}>
        <Text size="25px" marginBottom="10px" weight="500" color="#111b45">
          <span className="modal-title-25">
            {t("accountOwner.selectEmployees")}
          </span>
        </Text>
        <div className={"search-box w-100 " + styles["search-box"]}>
          <input
            type="text"
            placeholder={t("accountOwner.searchEmployee")}
            value={search}
            onChange={(e) => onSearch(e.currentTarget.value)}
          />
          <span className="ico">
            <img
              src={require("assets/images/search-icon.svg").default}
              alt="icon"
            />
          </span>
        </div>
        {/* Filter dropdowns */}
        <div className={`${styles["select_box_wrapper"]} ${styles["select-staff-modal-drop-box"]} mb-3`}>

            <Select
              className={"react-select-container"}
              classNamePrefix="react-select"
              placeholder="Select Office"
              options={officeList}
              value={selectedOffice}
              onChange={(val) => setSelectedOffice(val ?? defaultOfficeValue)}
              isSearchable={false}
            />
          <Select
            className={`react-select-container`}
            classNamePrefix="react-select"
            placeholder="Select Role"
            options={rolesData}
            value={selectedRole}
            onChange={(val) => setSelectedRole(val ?? defaultRoleValue)}
            isSearchable={false}
          />
        </div>
        <Text
          size="12px"
          marginBottom="24px"
          marginTop="24px"
          weight="400"
          color="rgb(111, 119, 136)"
        >
          {selectedMembers?.length} {t("Selected")}
        </Text>

        {/* Loading indicator */}
        {(isLoadingApi||isFetchingApi) ? (
          <div className="loader-container text-center">
            <Spinner animation="border" className="loader-spinner" />
          </div>
        ):<>

        {/* Select all checkbox */}
        {filteredMembers.length > 0 && (
          <div className="ch-checkbox">
            <label>
              <input
                type="checkbox"
                checked={
                  filteredMembers.length > 0 &&
                  filteredMembers.every((member) => {
                    const memberId = member?.userId;
                    return selectedMembers.some(
                      (selected) => Number(selected.userId) === Number(memberId)
                    );
                  })
                }
                onChange={toggleSelectAll}
              />
              <span> {t("staffTimesheet.selectAll")}</span>
            </label>
          </div>
        )}

        <ul className={"modal-employee-list " + styles["employee-list"]}>
          {filteredMembers.length > 0 &&
            filteredMembers.map((val, key) => {
              
              // Handle both API data structure and prop data structure
              const firstName = val?.firstName || val?.user?.firstName || "";
              const lastName = val?.lastName || val?.user?.lastName || "";
              const profilePic = val?.profilePic || val?.user?.profilePic;
              return (
                <li key={key}>
                  <div className="ch-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={checkMemberRoleAvaliable(selectedMembers, val)}
                        onChange={() => checkMember(val)}
                      />
                      <span className="d-flex align-items-center">
                        <img
                          src={profilePic || userDefaultImage}
                          onError={addDefaultSrc}
                          alt="profile"
                        />
                        <div>
                          {firstName} {lastName}
                        </div>
                      </span>
                    </label>
                  </div>
                </li>
              );
            })}
          {!isLoadingApi && filteredMembers.length === 0 && (
            <li>
              <div className="ch-checkbox">
                <label>{t("staffLeaves.noStaffAdded")}</label>
              </div>
            </li>
          )}
        </ul>
        </>}
        {selectionError && (
          <Text
            size="12px"
            marginBottom="12px"
            weight="400"
            color="#EE4F4F"
            role="alert"
          >
            {selectionError}
          </Text>
        )}
        <button
          className="button button-round button-shadow mr-sm-3 mb-3 w-sm-100"
          title={t("accountOwner.addEmployees")}
          disabled={confirmLoading}
          onClick={() => {
            if (!isManagerOnly && selectedMembers.length === 0) {
              setSelectionError(
                t("officePolicyManagement.selectAtLeastOneAssignee"),
              );
              return;
            }
            setSelectionError("");
            setMemberIds(selectedMembers);
            if (dontCloseModal) return;
            setIsSaveEmployeeModalOpen(false);
          }}
        >
          {confirmLoading ? <Spinner animation="border" className="loader-spinner" /> : t("questionnaire.select")}
        </button>
        <button
          className="button button-round button-border btn-mobile-link button-dark mb-md-3"
          onClick={() => setIsSaveEmployeeModalOpen(false)}
          title={t("cancel")}
        >
          {t("cancel")}
        </button>
        
      </ModalBody>
    </Modal>
  );
}

export default withTranslation()(SelectEmployeeForPolicyVersionModal);
